export const OWNER_ONLY_COMPANY_SETTINGS_MESSAGE =
  'Only the company owner can change these settings.';
export const OWNER_OR_SUPERVISOR_COMPANY_SETTINGS_MESSAGE =
  'Only the owner or a supervisor can change this setting.';

/** Shared copy for offline / unreachable backend errors across HuaKya. */
export const NETWORK_CONNECTION_LOST_MESSAGE =
  'Network connection lost. Check your connection and try again.';

function looksLikeTechnicalFirebaseError(raw: string): boolean {
  return (
    raw.includes('cloud_firestore/') ||
    raw.includes('firebase_storage/') ||
    raw.includes('firebase_auth/') ||
    raw.includes('functions/') ||
    raw.includes('[firebase')
  );
}

function looksLikeConnectivityFailure(err: unknown): boolean {
  const code =
    err && typeof err === 'object' && 'code' in err
      ? String((err as { code?: string }).code || '').toLowerCase()
      : '';
  if (
    code === 'unavailable' ||
    code === 'deadline-exceeded' ||
    code === 'network-request-failed' ||
    code === 'functions/unavailable' ||
    code === 'auth/network-request-failed' ||
    code === 'storage/retry-limit-exceeded'
  ) {
    return true;
  }

  const raw =
    err instanceof Error
      ? err.message
      : err && typeof err === 'object' && 'message' in err
        ? String((err as { message?: string }).message || '')
        : String(err ?? '');
  const lower = raw.toLowerCase();
  return (
    (lower.includes('network') &&
      (lower.includes('failed') ||
        lower.includes('error') ||
        lower.includes('unreachable') ||
        lower.includes('offline') ||
        lower.includes('connection'))) ||
    lower.includes('failed to fetch') ||
    lower.includes('load failed') ||
    lower.includes('networkerror') ||
    lower.includes('err_internet_disconnected') ||
    lower.includes('err_network_changed') ||
    lower.includes('err_connection') ||
    (lower.includes('timeout') &&
      (lower.includes('network') ||
        lower.includes('fetch') ||
        lower.includes('firestore') ||
        lower.includes('firebase')))
  );
}

/** Maps Firebase and other backend errors to copy safe to show in the UI. */
export function userFacingErrorMessage(
  err: unknown,
  {
    fallback,
    permissionDenied,
  }: {
    fallback: string;
    permissionDenied?: string;
  },
): string {
  if (looksLikeConnectivityFailure(err)) {
    return NETWORK_CONNECTION_LOST_MESSAGE;
  }

  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code?: string }).code || '');
    const message = String((err as { message?: string }).message || '').trim();

    if (code === 'permission-denied' || code === 'functions/permission-denied') {
      return permissionDenied ?? (message && !looksLikeTechnicalFirebaseError(message)
        ? message
        : 'You do not have permission to do that.');
    }
    if (code === 'unauthenticated' || code === 'functions/unauthenticated') {
      return 'Please sign in again.';
    }
    if (message && !looksLikeTechnicalFirebaseError(message)) {
      return message;
    }
  }

  if (err instanceof Error) {
    const message = err.message.trim();
    if (message && !looksLikeTechnicalFirebaseError(message)) {
      return message;
    }
  }

  return fallback;
}
